import React, { FC, useMemo, useState } from 'react';
import _ from 'lodash';
import { Text, PopoverMenu, TextButton } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { ChevronDown } from '@wix/wix-ui-icons-common';
import { useTemplatesContext } from '@components/Contexts/TemplatesContext';
import { SortingMethod, ValidSortingMethods } from '@common/types';
import { updateQueryParams } from '@utils/route';
import { useShallowRouter } from '@components/Hooks/useRouter';
import { useTemplatesBi } from '@components/Hooks/useTemplatesBi';
import { useExperiments } from '@wix/fe-essentials-standalone';
import { getSortFilterOptions } from '@utils/filterUtils';
import { ENABLE_TEMPLATES_SORT_BY_BEST_SELLER } from '@utils/constants';
import s from './SortMenu.module.scss';

const SortMenu: FC = () => {
  const { t } = useTranslation();
  const { navigate, router } = useShallowRouter();
  const {
    state: { sort },
  } = useTemplatesContext();
  const [selectedSort, setSelectedSort] = useState<SortingMethod>(sort);
  const { logBiClick } = useTemplatesBi({ is_mobile: false });

  const { experiments } = useExperiments({ readOnly: true });

  const isSortByBestSellerSpecEnabled = experiments.enabled(
    ENABLE_TEMPLATES_SORT_BY_BEST_SELLER,
  );

  const options = useMemo(() => {
    return getSortFilterOptions(t, isSortByBestSellerSpecEnabled);
  }, [t, isSortByBestSellerSpecEnabled]);

  const sortingMenuOnClick = (sortingMethod: SortingMethod) => {
    navigate(
      updateQueryParams(
        {
          sort: sortingMethod,
        },
        router,
      ),
    );
    setSelectedSort(sortingMethod);
  };

  return (
    <PopoverMenu
      dataHook="templates-sort-menu"
      fixed={false}
      triggerElement={
        <TextButton
          className={s.sortMenuButton}
          skin="dark"
          size="medium"
          suffixIcon={<ChevronDown />}
          dataHook="templates-sort-popover-trigger"
        >
          {t('marketplace.templates-page.sort-by')}&nbsp;
          <Text
            size="medium"
            dataHook="templates-sort-menu-title"
            onClick={() => {
              logBiClick({
                click_type: `Sort by`,
              });
            }}
            className={s.sortMenuButtonText}
          >
            {t(
              `marketplace.templates-page.${_.toLower(
                selectedSort || ValidSortingMethods.RELEVANCE,
              )}`,
            )}
          </Text>
        </TextButton>
      }
    >
      {_.map(options, (option) => (
        <PopoverMenu.MenuItem
          key={option.id}
          dataHook={`templates-sort-menu-item-${option.id}`}
          onClick={() => {
            logBiClick({
              click_type: `Sort by - ${option.id.split('_').join(' ')}`,
            });
            sortingMenuOnClick(option.id);
          }}
          text={option.value}
        />
      ))}
    </PopoverMenu>
  );
};

export default SortMenu;
